// Session 55 Part C: the service switches, shared by Add client and Edit
// client so the two screens cannot drift. Leena: "it would be good for me to
// be able to put in what I have, what I'm turning on, how often, and the modes
// that they're going to be in, instead of you having to do it."
//
// Modes are stored as entitlement codes (modules/index.js reads them back):
//   SEO recommendations  off | preview -> 'seo_recommendations_preview' | on -> 'seo_recommendations'
//   AI courses           off | preview -> 'ai_courses_preview'          | on -> 'ai_courses'
//   Newsletter           off | on -> 'newsletter'   (Wave 13 decision 11: no preview)
//   GMB Audit            off | on -> 'gmb_audit'
// Analytics is never set here: it switches on by itself once the connection
// check passes (Session 12 Part A).

// Session 23: SEO recommendations switch
export const SEO_RECS_MODES = [['off', 'Off'], ['preview', 'Preview (locked)'], ['on', 'On']];
export const SEO_RECS_HINT = {
  off: 'Hidden from the client\'s menu.',
  preview: 'The client sees the top 5 issues and the counts, with a "Contact Digital Lotus" button. No email on release.',
  on: 'The full checklist, page by page, with step-by-step instructions.',
};

// Session 28: the AI courses switch. Same three modes as SEO recommendations,
// and the same reason for being its own control rather than a checkbox: the
// middle state is a real one, not an absence.
export const COURSE_MODES = [['off', 'Off'], ['preview', 'Preview (locked)'], ['on', 'On']];
export const COURSE_HINT = {
  off: 'Hidden from the client\'s menu.',
  preview: 'The client sees what the course covers, the full lesson list locked, and one free sample lesson. Nothing they do in it is recorded.',
  on: 'The whole course, with their own progress and prompt library.',
};

// Session 16 / Session 57 (Wave 18): pack.blog.focus, on Add client and Edit
// client from this one list so the two screens say the same thing.
export const BLOG_FOCUS = [
  ['seo', 'SEO', 'Search-led posts from the keyword map and Search Console (the default).'],
  ['brand', 'Brand', 'Trending and seasonal topics, case studies, brand stories and process pieces. No keyword targeting.'],
  ['mixed', 'Mixed', 'About half brand pieces, half search-led.'],
];

const MODE_VALUES = new Set(['off', 'preview', 'on']);
const asMode = (v) => (MODE_VALUES.has(v) ? v : 'off');

/** What portal_create_client accepts (its allowed set is seo_blogs, analytics,
 *  gmb_audit and the two SEO recommendations codes; newsletter and courses are
 *  refused there and go through servicesPatch right after create). seo_blogs
 *  is always on. */
export function createEntitlements({ gmbAudit = false, seoRecs = 'off' } = {}) {
  const out = ['seo_blogs'];
  if (gmbAudit) out.push('gmb_audit');
  const s = asMode(seoRecs);
  if (s === 'on') out.push('seo_recommendations');
  if (s === 'preview') out.push('seo_recommendations_preview');
  return out;
}

/** The portal_update_client patch that switches on what create cannot:
 *  the newsletter module and the AI courses mode. Null when neither is set,
 *  so a blogs-only client needs no second call. The entitlements list is the
 *  module list Edit client sends (the RPC keeps the SEO recommendations codes
 *  already stored, s49). */
export function servicesPatch({ gmbAudit = false, newsletter = false, courses = 'off' } = {}) {
  const c = asMode(courses);
  if (!newsletter && c === 'off') return null;
  const patch = {};
  if (newsletter) patch.entitlements = ['seo_blogs', ...(gmbAudit ? ['gmb_audit'] : []), 'newsletter'];
  if (c !== 'off') patch.courses = c;
  return patch;
}

/** Off / Preview / On radios with the hint for the chosen mode. `name` must be
 *  unique on the page (radios group by it). */
export function ModeRadios({ name, modes, hints, value, onChange, disabled, after, hintClass = 'hint' }) {
  const v = asMode(value);
  return (
    <>
      {modes.map(([m, label]) => (
        <label key={m} className="check">
          <input type="radio" name={name} checked={v === m} disabled={disabled} onChange={() => onChange(m)} /> <span>{label}</span>
        </label>
      ))}
      <span className={hintClass}>{hints[v]}{after ? ` ${after}` : ''}</span>
    </>
  );
}
